import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './data/supabaseClient';
import { AuthContext } from './Provider.js';
import { useTranslation } from 'react-i18next';
import { useToast } from './hooks/ToastContext';
import {
  FaArrowLeft,
  FaRoute,
  FaLocationArrow,
  FaSearchLocation,
  FaUser,
  FaBox,
  FaCalendarAlt,
  FaClock,
  FaRuler,
  FaCarSide,
  FaExclamationTriangle,
  FaUsers,
  FaInfoCircle,
  FaWeightHanging,
  FaShippingFast,
  FaRegPaperPlane,
  FaCar
} from 'react-icons/fa';
import { format } from 'date-fns';
import RouteMatches from './RouteMatches';
import { CustomButton8, CustomButton7 } from './Templates';
import { useChat } from './Chat/ChatContext';
// Header removed - already rendered in App.js
import Spinner from './common/Spinner';
import { getCorrectRouteEndpoints } from '../utils/routeUtils';
import { styleStaticMapUrl } from '../utils/mapStyle';
import { sendNewConversationNotification } from './Notifications/notificationService';
import './PostDetails.css';

// Helper function to parse coordinates from PostgreSQL point format or text
const parseCoordinates = (coordString) => {
  if (!coordString) return null;
  
  try {
    // Handle PostgreSQL point format: "(lng,lat)"
    if (coordString.startsWith('(') && coordString.endsWith(')')) {
      const coords = coordString.substring(1, coordString.length - 1).split(',');
      if (coords.length === 2) {
        // For Google Maps, we need "lat,lng" format
        return `${coords[1].trim()},${coords[0].trim()}`;
      }
    }
    
    // Handle text address (let Google geocode it)
    return coordString;
  } catch (error) {
    console.error('Error parsing coordinates:', error, coordString);
    return coordString; // Return as is, let Google Maps API handle it
  }
};

// Helper functions for safe numeric handling
const safeParseFloat = (value) => {
  if (value === null || value === undefined) return 0;
  try {
    const result = parseFloat(value);
    return isNaN(result) ? 0 : result;
  } catch (error) {
    console.error('Error parsing float:', error, value);
    return 0;
  }
};

const formatFloat = (value, precision = 1) => {
  return safeParseFloat(value).toFixed(precision);
};

export default function PostDetails({ type: propType }) {
  const { id } = useParams(); // Extract id from URL params
  const location = useLocation(); // Get location object to access query params
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { openChatWithConversation, checkCanStartChat } = useChat();
  
  // Get type from query params if available, otherwise use prop
  const queryParams = new URLSearchParams(location.search);
  const queryType = queryParams.get('type');
  const type = queryType || propType; // Use query param type if available, otherwise use prop type
  
  const [postData, setPostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  
  // Fetch post, package, or request data
  useEffect(() => {
    console.log('PostDetails initialized with type:', type, 'and id:', id);
    const fetchData = async () => {
      if (!id) {
        setError('No ID specified');
        setLoading(false);
        return;
      }
      
      // Safely handle undefined type with default value
      const safeType = type || 'post';

      try {
        setLoading(true);
        setError(null);
        
        // Log the type and ID for debugging
        console.log('Fetching data with type:', type, 'and id:', id);
        
        const isPackage = safeType === 'package';
        const isRequest = safeType === 'request';
        let table;
        
        if (isPackage) {
          table = 'packages';
        } else if (isRequest) {
          table = 'event_ride_requests';
        } else {
          table = 'posts';
        }
        
        console.log('Using table:', table);
        
        let query;
        
        if (isRequest) {
          // For ride requests, we need to join with profiles, but using a different approach
          query = supabase
            .from(table)
            .select(`
              *,
              events(*)
            `)
            .eq('id', parseInt(id, 10));
            
          console.log('Executing request query:', JSON.stringify(query));
            
          // After we get the request, we'll fetch the passenger profile separately
        } else {
          // For posts and packages
          query = supabase
            .from(table)
            .select(`
              *,
              profiles(username, avatar_url)
            `);
          
          if (isPackage) {
            // For packages, use UUID as is
            console.log('Querying packages table with id:', id);
            console.log('isPackage is true, using ID as UUID, type =', typeof id);
            query = query.eq('id', id);
          } else {
            // For posts, ensure ID is an integer and handle potential NaN values
            const postId = parseInt(id, 10);
            if (isNaN(postId)) {
              throw new Error('Invalid post ID');
            }
            query = query.eq('id', postId);
          }
        }
          
        console.log('Executing query:', query);
        // Check if the query returns any data first without using .single()
        const { data: checkData, error: checkError } = await query;
        
        if (checkError) {
          console.error('Error checking data:', checkError);
          throw checkError;
        }
        
        console.log('Check query returned:', checkData?.length || 0, 'rows', checkData);
        
        if (!checkData || checkData.length === 0) {
          setError(`${isRequest ? 'Request' : isPackage ? 'Package' : 'Post'} not found (ID: ${id})`);
          setLoading(false);
          return;
        }
        
        if (checkData.length > 1) {
          console.warn('Multiple rows returned, using first item');
        }
        
        // Use the first item if multiple were returned
        const data = checkData[0];
          
        // fetchError handling removed as we're not using .single() anymore
        
        if (!data) {
          setError(`${isPackage ? 'Package' : 'Post'} not found`);
          setLoading(false);
          return;
        }
        
        // Add markers to indicate the content type
        data.isPackage = isPackage;
        data.isRequest = isRequest;
        
        if (isRequest) {
          // For requests, we need to fetch the passenger profile separately
          try {
            // First, get the passenger_id from the request data
            const passengerId = data.passenger_id;
            
            if (passengerId) {
              // Fetch the passenger profile
              const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('id, username, avatar_url')
                .eq('id', passengerId)
                .single();
                
              if (profileError) {
                console.error('Error fetching passenger profile:', profileError);
              }
              
              if (profileData) {
                // Add the profile data to our request data
                data.passenger_profile = profileData;
              }
            }
          } catch (profileFetchError) {
            console.error('Error in passenger profile fetch:', profileFetchError);
          }
          
          data.role = 'Request';
          // For ride requests, adjust the data structure to be compatible with the UI
          data.profiles = data.passenger_profile || {};
          data.address_from = data.pickup_location || '';
          data.address_to = data.events?.[0]?.location || '';
          data.event = data.events?.[0] || {};
          data.travel_date = data.events?.[0]?.start_date || new Date().toISOString();
        } else if (isPackage) {
          data.role = 'Package';
        }
        
        console.log('Fetched data:', data);
        // Additional debugging for ride requests
        if (isRequest) {
          console.log('Ride request pickup_coordinates:', data.pickup_coordinates);
          console.log('Ride request event data:', data.events?.[0]);
        }
        
        // Fix route endpoints if this is a post or package with waypoints
        if (!isRequest) {
          try {
            const correctedData = await getCorrectRouteEndpoints(data, isPackage);
            console.log('Route endpoints corrected for PostDetails:', correctedData.route_corrected ? 'Yes' : 'No');
            setPostData(correctedData);
          } catch (routeError) {
            console.error('Error correcting route endpoints in PostDetails:', routeError);
            setPostData(data);
          }
        } else {
          setPostData(data);
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, type]);
  
  const handleBackClick = () => {
    // Check if there's a previous page in history
    if (window.history.length > 1) {
      navigate(-1); // Go back to the previous page
    } else {
      navigate('/home'); // No history, go to home
    }
  };
  
  const formatDate = (date) => {
    if (date) {
      return format(new Date(date), "dd/MM/yyyy");
    }
    return t('posts.not_specified');
  };

  const formatTime = (time) => {
    if (!time) return t('posts.not_specified');
    // Convert "HH:mm:ss" to "HH:mm"
    return time.split(':').slice(0, 2).join(':');
  };

  const formatAddress = (street, city, country) => {
    const parts = [street, city, country].filter(Boolean);
    return parts.join(", ");
  };
  
  const handleMatchClick = (match) => {
    console.log('Selected match:', match);
    // Ensure all numeric values are properly parsed
    const processedMatch = {
      ...match,
      distance_km: typeof match.distance_km === 'string' ? parseFloat(match.distance_km) : match.distance_km,
      start_point_lat: typeof match.start_point_lat === 'string' ? parseFloat(match.start_point_lat) : match.start_point_lat,
      start_point_lng: typeof match.start_point_lng === 'string' ? parseFloat(match.start_point_lng) : match.start_point_lng,
      end_point_lat: typeof match.end_point_lat === 'string' ? parseFloat(match.end_point_lat) : match.end_point_lat,
      end_point_lng: typeof match.end_point_lng === 'string' ? parseFloat(match.end_point_lng) : match.end_point_lng
    };
    setSelectedMatch(processedMatch);
  };
  
  // Use the checkCanStartChat function from ChatContext
  
  const handleMessageClick = async (recipient) => {
    if (!user) {
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.login_required')
      });
      navigate('/login');
      return;
    }
    
    try {
      // Get the recipient data needed
      const isRecipientPackage = recipient.match_type === 'package';
      const recipientId = isRecipientPackage ? recipient.match_id : parseInt(recipient.match_id, 10);
      const recipientUserId = recipient.user_id;
      
      // Show loading state
      setLoading(true);
      
      console.log('Starting new conversation with:', {
        current_user_id: user.id,
        other_user_id: recipientUserId,
        post_id: isRecipientPackage ? null : recipientId,
        package_id: isRecipientPackage ? recipientId : null
      });
      
      // Check if user has reached their conversation limit
      const { canChat, limitData, error: chatLimitError } = await checkCanStartChat();
      
      if (chatLimitError) {
        console.error('Error checking chat limits:', chatLimitError);
      }
      
      if (!canChat) {
        // Show upgrade prompt if chat limit reached
        addToast({
          type: 'error',
          title: t('chat.limit_reached_title'),
          description: t('chat.limit_reached_message', { max: limitData?.max_allowed || 5 })
        });
        
        const upgradeNow = window.confirm(t('chat.upgrade_subscription_prompt'));
        if (upgradeNow) {
          navigate('/subscription');
        }
        setLoading(false);
        return;
      }
      
      // Create conversation with RPC function
      try {
      const { data: result, error: startError } = await supabase.rpc(
      'start_or_get_conversation',
      {
      current_user_id: user.id,
      other_user_id: recipientUserId,
      post_id: isRecipientPackage ? null : recipientId,
      package_id: isRecipientPackage ? recipientId : null,
      message_text: null
      }
      );
      
      if (startError) {
      console.error('Error from start_or_get_conversation:', startError);
      throw startError;
      }
      
      console.log('Conversation created/retrieved:', result);
        
        // Check if we have remaining_chats in the result - this is from our updated function
        if (result.remaining_chats !== undefined) {
          console.log('Remaining chats from server:', result.remaining_chats);
          
          // Optionally show this info to the user
          if (result.is_new) {
            addToast({
              type: 'info',
              title: t('chat.conversation_created'),
              description: t('chat.remaining_conversations', { count: result.remaining_chats })
            });
          }
        }
        
        if (!result?.conversation_id) {
          // This could happen if limit was reached (should be caught by our client check)
          // or if there was some other error
          const errorMessage = result?.error || 'Failed to create conversation';
          console.error('Failed to create conversation:', errorMessage);
          throw new Error(errorMessage);
        }
        
        // Create notification for new conversations using templated service
        if (result.is_new) {
          try {
            // Get sender's name from profile
            const { data: senderProfile } = await supabase
              .from('profiles')
              .select('full_name, username')
              .eq('id', user.id)
              .single();
            
            const senderName = senderProfile?.full_name || 
                              senderProfile?.username || 
                              user?.user_metadata?.full_name || 
                              user?.user_metadata?.name || 
                              'Someone';
            
            // Use the new templated notification service
            const notificationResult = await sendNewConversationNotification(
              result.conversation_id,
              user.id,
              recipientUserId,
              senderName
            );
            
            if (!notificationResult.success) {
              console.warn('Failed to send conversation notification:', notificationResult.error);
            }
          } catch (notificationError) {
            console.error('Error sending conversation notification:', notificationError);
            // Don't fail the conversation creation if notification fails
          }
        }
        
        // Open the chat
        openChatWithConversation(result.conversation_id);
        setLoading(false);
        
        // Update any chat counter displays
        if (result.remaining_chats !== undefined) {
          // You can dispatch an event or use a state management system
          // to update any chat counters displayed elsewhere in the app
          const chatCountEvent = new CustomEvent('chatCountUpdated', {
            detail: {
              remaining: result.remaining_chats,
              limit: result.monthly_limit,
              count: result.monthly_count
            }
          });
          window.dispatchEvent(chatCountEvent);
        }
      } catch (rpcError) {
        console.error('RPC function failed:', rpcError);
        
        // Instead of a complex fallback process that may cause double-counting,
        // we'll simply show an error and ask the user to try again
        addToast({
          type: 'error',
          title: t('errors.conversation_error'),
          description: t('errors.please_try_again')
        });
        
        setLoading(false);
        return;
      }
      
    } catch (error) {
      console.error('Error starting conversation:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('chat.error_conversation_start')
      });
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="pd-post-details-loading">
        <Spinner size="medium" customText={t('app.loading')} />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="pd-post-details-error">
        <div className="pd-back-button">
          <CustomButton8 onClick={handleBackClick}>
            <FaArrowLeft />
          </CustomButton8>
        </div>
        <div className="pd-error-message">
          <FaExclamationTriangle />
          <p>{error}</p>
        </div>
      </div>
    );
  }
  
  if (!postData) {
    return (
      <div className="pd-post-details-error">
        <div className="pd-back-button">
          <CustomButton8 onClick={handleBackClick}>
            <FaArrowLeft />
          </CustomButton8>
        </div>
        <div className="pd-error-message">
          <FaExclamationTriangle />
          <p>{t('posts.not_found')}</p>
        </div>
      </div>
    );
  }
  
  const isOwnPost = user?.id === postData.user_id;
  
  // Handler for sending message to post owner
  const handleSendMessageToOwner = async () => {
    if (!user) {
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.login_required')
      });
      navigate('/login');
      return;
    }
    
    try {
      setLoading(true);
      
      // Check if user can start a new chat
      const { canChat, limitData, error: chatLimitError } = await checkCanStartChat();
      
      if (chatLimitError) {
        console.error('Error checking chat limits:', chatLimitError);
      }
      
      if (!canChat) {
        addToast({
          type: 'error',
          title: t('chat.limit_reached_title'),
          description: t('chat.limit_reached_message', { max: limitData?.max_allowed || 5 })
        });
        
        const upgradeNow = window.confirm(t('chat.upgrade_subscription_prompt'));
        if (upgradeNow) {
          navigate('/subscription');
        }
        setLoading(false);
        return;
      }
      
      // Start conversation
      const { data: result, error: startError } = await supabase.rpc(
        'start_or_get_conversation',
        {
          current_user_id: user.id,
          other_user_id: postData.user_id,
          post_id: postData.isPackage ? null : postData.id,
          package_id: postData.isPackage ? postData.id : null,
          message_text: null
        }
      );
      
      if (startError) {
        console.error('Error starting conversation:', startError);
        throw startError;
      }
      
      if (!result?.conversation_id) {
        throw new Error('Failed to create conversation');
      }
      
      // Show success message
      if (result.is_new) {
        addToast({
          type: 'success',
          title: t('chat.conversation_created'),
          description: t('chat.started_with_user', { username: postData.profiles?.username || t('chat.unknown_user') })
        });
        
        // Create notification for new conversation
        try {
          const { data: senderProfile } = await supabase
            .from('profiles')
            .select('full_name, username')
            .eq('id', user.id)
            .single();
          
          const senderName = senderProfile?.full_name || 
                            senderProfile?.username || 
                            user?.user_metadata?.full_name || 
                            user?.user_metadata?.name || 
                            'Someone';
          
          await sendNewConversationNotification(
            result.conversation_id,
            user.id,
            postData.user_id,
            senderName
          );
        } catch (notificationError) {
          console.error('Error sending notification:', notificationError);
        }
      }
      
      // Open the chat
      openChatWithConversation(result.conversation_id);
      setLoading(false);
      
    } catch (error) {
      console.error('Error starting conversation:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('chat.error_conversation_start')
      });
      setLoading(false);
    }
  };
  
  return (
    <>
      <div className="pd-post-details-container">
        <div className="pd-back-button">
          <CustomButton8 onClick={handleBackClick}>
            <FaArrowLeft />
          </CustomButton8>
        </div>
        
        <div className="pd-post-details-content">
          <h2>
            {postData.isPackage ? (
              <FaBox className="pd-header-icon" />
            ) : postData.isRequest ? (
              <FaUsers className="pd-header-icon" />
            ) : postData.role === 'Driver' ? (
              <FaCarSide className="pd-header-icon" />
            ) : (
              <FaUsers className="pd-header-icon" />
            )}
            {postData.isPackage ? t('posts.package_details') :
             postData.isRequest ? t('posts.ride_request_details') :
             postData.role === 'Driver' ? t('posts.driver_post_details') :
             postData.role === 'Passenger' ? t('posts.passenger_post_details') :
             t('posts.post_details')}
          </h2>
          
          <div className={`pd-post-card ${postData.isPackage ? 'pd-package-card' : 'pd-regular-card'}`}>
            {/* User info */}
            <div className="pd-user-name">
              <img
                src={postData.profiles?.avatar_url || '/default-avatar.png'}
                className="pd-avatar"
                alt={postData.profiles?.username || t('chat.unknown_user')}
              />
              <h3>{postData.profiles?.username || t('chat.unknown_user')}</h3>
            </div>
            
            {/* Post badge */}
            <div className={`pd-post-type-badge ${postData.isPackage ? 'pd-package-badge' : postData.role === 'Driver' ? 'pd-driver-badge' : postData.role === 'Passenger' ? 'pd-passenger-badge' : ''}`}>
              {postData.isPackage ? t('posts.package') :
               postData.role === 'Driver' ? t('posts.driver') :
               postData.role === 'Passenger' ? t('posts.passenger') : postData.role}
            </div>
            
            {/* Map */}
              <div className="pd-map-container">
                {postData.isRequest ? (
                  // For ride requests, we need to generate a static map URL based on pickup location and event location
                  <div className="pd-request-map">
                    {(postData.pickup_coordinates || postData.pickup_location) && postData.event ? (
                      <img
                        src={`https://maps.googleapis.com/maps/api/staticmap?size=600x300&markers=color:green|label:A|${parseCoordinates(postData.pickup_coordinates) || postData.pickup_location || ''}&markers=color:red|label:B|${parseCoordinates(postData.event?.location_coordinates) || postData.event?.location || ''}&path=color:0x0000FF|weight:5|${parseCoordinates(postData.pickup_coordinates) || postData.pickup_location || ''}|${parseCoordinates(postData.event?.location_coordinates) || postData.event?.location || ''}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}
                        alt={t('chat.route_map')}
                        className="pd-route-map"
                        onError={(e) => {
                          e.target.src = '/placeholder-map.jpg';
                          console.error('Error loading Google Maps image');
                        }}
                      />
                    ) : (
                      <img
                        src={styleStaticMapUrl(postData.map_image_url) || '/placeholder-map.jpg'}
                        alt={t('chat.route_map')}
                        className="pd-route-map"
                      />
                    )}
                  </div>
                ) : (
                  // For regular posts or packages
                  <img
                    src={styleStaticMapUrl(postData.map_image_url) || '/placeholder-map.jpg'}
                    alt={t('chat.route_map')}
                    className="pd-route-map"
                  />
                )}
              </div>
            
            {/* Route details */}
            <div className="pd-post-content">
              <div className="pd-post-detail">
                <span className="pd-detail-icon"><FaRoute /></span>
                <div className="pd-detail-content">
                  <div className="pd-detail-label">{t('posts.route')}:</div>
                  <div className="pd-location-detail">
                    <span className="pd-location-marker pd-from">A</span>
                    <span className="pd-location-text">
                      {postData.isRequest ? 
                        postData.pickup_location :
                        formatAddress(
                          postData.address_from_street,
                          postData.address_from_city,
                          postData.address_from_country
                        )
                      }
                    </span>
                  </div>
                  <div className="pd-location-detail">
                    <span className="pd-location-marker pd-to">B</span>
                    <span className="pd-location-text">
                      {postData.isRequest ? 
                        (postData.event?.location || 'Event location') :
                        formatAddress(
                          postData.address_to_street,
                          postData.address_to_city,
                          postData.address_to_country
                        )
                      }
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="pd-post-info-grid">
                {/* Common details */}
                <div className="pd-post-detail">
                  <span className="pd-detail-icon"><FaCalendarAlt /></span>
                  <div className="pd-detail-content">
                    <div className="pd-detail-label">{t('posts.date')}:</div>
                    <div>{formatDate(postData.travel_date)}</div>
                  </div>
                </div>
                
                <div className="pd-post-detail">
                  <span className="pd-detail-icon"><FaClock /></span>
                  <div className="pd-detail-content">
                    <div className="pd-detail-label">{t('posts.time')}:</div>
                    <div>{postData.isRequest ? formatTime(new Date(postData.travel_date).toTimeString()) : formatTime(postData.travel_time)}</div>
                  </div>
                </div>
                
                {postData.isRequest && postData.event && (
                  <div className="pd-post-detail">
                    <span className="pd-detail-icon"><FaInfoCircle /></span>
                    <div className="pd-detail-content">
                      <div className="pd-detail-label">Event:</div>
                      <div>{postData.event.title}</div>
                    </div>
                  </div>
                )}
                
                {/* Post-specific details */}
                {!postData.isPackage && (
                  <>
                    {postData.car && (
                      <div className="pd-post-detail">
                        <span className="pd-detail-icon"><FaCar /></span>
                        <div className="pd-detail-content">
                          <div className="pd-detail-label">{t('posts.car')}:</div>
                          <div>{postData.car}</div>
                        </div>
                      </div>
                    )}
                    
                    {postData.seats && (
                      <div className="pd-post-detail">
                        <span className="pd-detail-icon"><FaUser /></span>
                        <div className="pd-detail-content">
                          <div className="pd-detail-label">{t('posts.seats')}:</div>
                          <div>{postData.seats}</div>
                        </div>
                      </div>
                    )}
                    
                    <div className="pd-post-detail">
                      <span className="pd-detail-icon"><FaRoute /></span>
                      <div className="pd-detail-content">
                        <div className="pd-detail-label">{t('posts.drive_type')}:</div>
                        <div>
                          {postData.travel_type ? 
                            t(`posts.${(postData.travel_type || '').toLowerCase().replace('-', '_')}`) : 
                            t('posts.not_specified')}
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                {/* Package-specific details */}
                {postData.isPackage && (
                  <>
                    <div className="pd-post-detail">
                      <span className="pd-detail-icon"><FaBox /></span>
                      <div className="pd-detail-content">
                        <div className="pd-detail-label">Size:</div>
                        <div>{postData.package_size}</div>
                      </div>
                    </div>
                    
                    <div className="pd-post-detail">
                      <span className="pd-detail-icon"><FaWeightHanging /></span>
                      <div className="pd-detail-content">
                        <div className="pd-detail-label">Weight:</div>
                        <div>{postData.package_weight}</div>
                      </div>
                    </div>
                    
                    <div className="pd-post-detail">
                      <span className="pd-detail-icon"><FaShippingFast /></span>
                      <div className="pd-detail-content">
                        <div className="pd-detail-label">Urgency:</div>
                        <div>{postData.delivery_urgency}</div>
                      </div>
                    </div>
                  </>
                )}
                
                {/* Description */}
                {postData.description && (
                  <div className="pd-post-detail pd-full-width">
                    <span className="pd-detail-icon"><FaInfoCircle /></span>
                    <div className="pd-detail-content">
                      <div className="pd-detail-label">{t('forms.description')}:</div>
                      <div>{postData.description}</div>
                    </div>
                  </div>
                )}
                
                {/* Notes for ride requests */}
                {postData.isRequest && postData.notes && (
                  <div className="pd-post-detail pd-full-width">
                    <span className="pd-detail-icon"><FaInfoCircle /></span>
                    <div className="pd-detail-content">
                      <div className="pd-detail-label">Notes:</div>
                      <div>{postData.notes}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Send Message Button - Show for posts that aren't your own */}
            {!isOwnPost && (
              <div className="pd-message-action-container">
                <CustomButton7
                  label={t('chat.send_message')}
                  onClick={handleSendMessageToOwner}
                >
                  <FaRegPaperPlane size={18} />
                </CustomButton7>
              </div>
            )}
          </div>
          
          {/* If this is the user's own post, show matches */}
          {isOwnPost && (
            <div className="pd-matches-section" style={{overflow: 'visible', width: '100%'}}>
              {postData.route_polyline ? (
                <RouteMatches
                  routeId={postData.id}
                  isPackage={postData.isPackage}
                  role={postData.isPackage ? null : (postData.role === 'Driver' ? 'Passenger' : 'Driver')}
                  radiusKm={postData.search_radius_km || 10}
                  onSelectMatch={handleMatchClick}
                />
              ) : (
                <div className="pd-no-route-data-warning">
                  <FaExclamationTriangle />
                  <h3>{t('warnings.no_route_data_title') || 'Route Matching Not Available'}</h3>
                  <p>
                    {t('warnings.no_route_data_message') || 'This post does not have route data needed for matching. To enable matching, please recreate this post and make sure to calculate the route.'}
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* If a match is selected, show its details */}
          {selectedMatch && (
            <div className="pd-selected-match-container">
              <h3>Selected Match</h3>
              
              <div className={`pd-match-card ${selectedMatch.match_type}-card`}>
                <div className="pd-match-header">
                  <div className="pd-match-user">
                    {selectedMatch.avatar_url ? (
                      <img src={selectedMatch.avatar_url} alt={selectedMatch.username} className="pd-avatar" />
                    ) : (
                      <FaUser className="pd-avatar-placeholder" />
                    )}
                    <span className="pd-username">{selectedMatch.username}</span>
                  </div>
                  <div className="pd-match-type">
                    {selectedMatch.match_role === 'Driver' && <FaCarSide className="pd-role-icon pd-driver" />}
                    {selectedMatch.match_role === 'Passenger' && <FaUser className="pd-role-icon pd-passenger" />}
                    {selectedMatch.match_role === 'Package' && <FaBox className="pd-role-icon pd-package" />}
                    <span>{selectedMatch.match_role === 'Driver' ? t('posts.driver') :
                           selectedMatch.match_role === 'Passenger' ? t('posts.passenger') :
                           selectedMatch.match_role === 'Package' ? t('posts.package') : selectedMatch.match_role}</span>
                  </div>
                </div>
                
                <div className="pd-match-route">
                  <div className="pd-route-point">
                    <FaLocationArrow className="icon" />
                    <span>From: {parseFloat(selectedMatch.start_point_lat || 0).toFixed(4)}, {parseFloat(selectedMatch.start_point_lng || 0).toFixed(4)}</span>
                  </div>
                  <div className="pd-route-point">
                    <FaSearchLocation className="icon" />
                    <span>To: {parseFloat(selectedMatch.end_point_lat || 0).toFixed(4)}, {parseFloat(selectedMatch.end_point_lng || 0).toFixed(4)}</span>
                  </div>
                </div>
                
                <div className="pd-match-details">
                  <div className="pd-match-detail">
                    <FaCalendarAlt className="icon" />
                    <span>{new Date(selectedMatch.travel_date).toLocaleDateString()}</span>
                  </div>
                  <div className="pd-match-detail">
                    <FaClock className="icon" />
                    <span>{selectedMatch.travel_time}</span>
                  </div>
                  <div className="pd-match-detail">
                    <FaRuler className="icon" />
                    <span>{parseFloat(selectedMatch.distance_km || 0).toFixed(1)} km away</span>
                  </div>
                </div>
                
                <div className="pd-match-actions">
                  <CustomButton7
                    label={t('chat.send_message')}
                    onClick={() => handleMessageClick(selectedMatch)}
                  >
                    <FaRegPaperPlane size={18} />
                  </CustomButton7>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}